<template>

    <div v-if="user" id="layout-app" :class="{ 'layout': true }">
        <v-the-alerts />
        <v-the-cookie-banner />
        <v-the-checklist />
        <transition name="modal">
            <v-modal-logout v-if="modal === 'logout'" />
            <v-modal-project-activate v-if="modal === 'project-activate'" />
            <v-modal-project-create v-if="modal === 'project-create'" />
            <v-modal-review-create v-if="modal === 'review-create-quick'" @saved="_refreshProject" />
            <v-modal-location-create v-if="modal === 'location-create-quick'" @saved="_refreshProject" />
            <v-modal-tag-create v-if="modal === 'tag-create-quick'" @saved="_refreshProject" />
            <v-modal-customer-create v-if="modal === 'customer-create-quick'" />
            <v-modal-cookie-preferences v-if="modal === 'cookie-preferences'" />
        </transition>
        <div :class="`secondary ${$store.getters.sidebarState}`">
            <v-the-sidebar />
        </div>
        <div class="primary">
            <transition name="fade" mode="out-in">
                <router-view :key="(project && project.id) + '-' + ($store.getters.locationId || '')"></router-view>
            </transition>
            <v-the-legal />
        </div>
    </div>

</template>

<style scoped lang="scss">

    html, body {
        height: 100%;
    }

    .layout {
        display: flex;
        flex-grow: 1;
        height: 100%;
        min-width: map-get($grid-breakpoints, 'xl');
        .secondary {
            flex-shrink: 0;
            position: relative;
            &.collapsed {
                width: $sidebar-width-collapsed;
            }
            &.expanded {
                width: $sidebar-width-expanded;
            }
        }
        .primary {
            background: $color-bg;
            display: flex;
            flex-direction: column;
            flex-grow: 1;
            height: 100%;
            min-height: 100%;
            min-width: 0;
            overflow-x: hidden;
            overflow-y: auto;
            position: relative;
        }
    }

</style>

<script>

import ModalCookiePreferences from '@modals/ModalCookiePreferences';
import ModalCustomerCreate from '@modals/ModalCustomerCreate';
import ModalLocationCreate from '@modals/ModalLocationCreate';
import ModalLogout from '@modals/ModalLogout';
import ModalProjectActivate from '@modals/ModalProjectActivate';
import ModalProjectCreate from '@modals/ModalProjectCreate';
import ModalReviewCreate from '@modals/ModalReviewCreate';
import ModalTagCreate from '@modals/ModalTagCreate';
import moment from 'moment';
import PusherUtil from '@utils/PusherUtil';
import ThemeUtil from '@utils/ThemeUtil';
import TheAlerts from '@cmp/TheAlerts';
import TheCookieBanner from '@cmp/TheCookieBanner';
import TheChecklist from '@cmp/TheChecklist';
import TheLegal from '@cmp/TheLegal';
import TheSidebar from '@cmp/TheSidebar';

export default {

    components: {
        'v-modal-cookie-preferences': ModalCookiePreferences,
        'v-modal-customer-create': ModalCustomerCreate,
        'v-modal-location-create': ModalLocationCreate,
        'v-modal-logout': ModalLogout,
        'v-modal-project-activate': ModalProjectActivate,
        'v-modal-project-create': ModalProjectCreate,
        'v-modal-review-create': ModalReviewCreate,
        'v-modal-tag-create': ModalTagCreate,
        'v-the-alerts': TheAlerts,
        'v-the-cookie-banner': TheCookieBanner,
        'v-the-checklist': TheChecklist,
        'v-the-legal': TheLegal,
        'v-the-sidebar': TheSidebar,
    },

    computed: {

        agency() {
            return this.$store.getters.agency;
        },

        user() {
            return this.$store.getters.user;
        },

        sidebar() {
            return this.$store.getters.sidebar;
        },

        isDarkMode() {
            return this.$store.getters.isDarkMode;
        },

        hasFunctionalConsent() {
            return this.$store.getters.hasFunctionalConsent;
        },

    },

    beforeMount() {
        const script = document.createElement('script');
        script.src = 'https://do.featurebase.app/js/sdk.js';
        script.id = 'featurebase-sdk';
        document.head.appendChild(script);
    },

    beforeDestroy() {
        ThemeUtil.unsetThemeForUser(this.user);
        PusherUtil.disconnect();
        PusherUtil.leave(`user.${this.user.id}`);
        if (this.project) {
            PusherUtil.leave(`project.${this.project.id}`);
        }
    },

    mounted() {
        // Set the theme.
        ThemeUtil.setThemeForUser(this.user);
        if (this.user.agency && typeof this.user.agency === 'object' && !Array.isArray(this.user.agency)) {
            ThemeUtil.setThemeForAgency(this.user.agency);
        }

        // Connect to Pusher.
        PusherUtil.connect();
        window.Echo.private(`user.${this.user.id}`)
            .listen('.user.updated', e => {
                this._refreshUser();
                this._refreshProject();
            })
            .listen('.user.alert.created', e => {
                this.$store.commit('setAlert', {
                    message: e.message,
                    type: e.type,
                });
            });

        // Listen for project updates.
        this.setupProjectListener();

        // Initialize HelpScout.
        this.initHelpScout();

        // Initialize FeatureBase.
        this.initFeatureBase();
    },

    watch: {

        project(newProject, oldProject) {
            if (oldProject && newProject && newProject.id !== oldProject.id) {
                PusherUtil.leave(`project.${oldProject.id}`);
                this.setupProjectListener();
            } else if (oldProject === undefined) {
                this.setupProjectListener();
            }
        },

        hasFunctionalConsent(val) {
            if (val) this.initHelpScout();
        },

    },

    methods: {

        setupProjectListener() {
            if (!this.project) return;
            this.$nextTick(() => {
                window.Echo.private(`project.${this.project.id}`)
                    .listen('.project.updated', e => {
                        // Only refresh if this is the currently selected project.
                        if (e.id === this.project.id) {
                            this._refreshProject();
                        }
                    });
            });
        },

        initFeatureBase() {
            if (this.user.is_sub_account) return;
            if (typeof window.Featurebase !== 'function') {
                window.Featurebase = function() {
                    (window.Featurebase.q = window.Featurebase.q || []).push(arguments);
                }
            }
            window.Featurebase('identify', {
                organization: 'moregoodreviews',
                email: this.user.email,
                name: this.user.name,
                id: String(this.user.id),
            });
            window.Featurebase('initialize_changelog_widget', {
                organization: 'moregoodreviews',
                placement: 'right',
                theme: this.isDarkMode ? 'dark' : 'light',
                fullscreenPopup: true,
                usersName: this.user.first_name,
            });
            window.Featurebase('initialize_survey_widget', {
                organization: 'moregoodreviews',
                placement: 'bottom-right',
                theme: this.isDarkMode ? 'dark' : 'light',
            });
        },

        initHelpScout() {
            if (this.user.is_sub_account || this.project.is_demo) return;
            const domain = window.location.hostname.toLowerCase().replace('www.', '');
            if (domain === process.env.VUE_APP_DOMAIN) {
                if (typeof window.Beacon !== 'undefined') {
                    window.Beacon('init', process.env.VUE_APP_HELPSCOUT_KEY);
                    window.Beacon('identify', {
                        name: this.user.name,
                        email: this.user.email,
                        signature: this.user.helpscout_signature,
                        user_id: String(this.user.id),
                        plan: this.user.plan ? this.user.plan.slug : 'free',
                        billing_id: this.user.billing_id !== null ? this.user.billing_id : '',
                        billing_plan: this.user.billing_plan !== null ? this.user.billing_plan : '',
                        billing_status: this.user.billing_status !== null ? this.user.billing_status : '',
                        agency: this.user.features.agency ? 'Yes' : 'No',
                        signed_up_at: moment.unix(this.user.created_at).format('YYYY-MM-DD'),
                        trial_ends_at: this.user.trial_ends_at ? moment.unix(this.user.trial_ends_at).format('YYYY-MM-DD') : '',
                        subscribed_at: this.user.subscribed_at ? moment.unix(this.user.subscribed_at).format('YYYY-MM-DD') : '',
                    });
                }
            }
        },

    },

}

</script>